import { tx as translateInterfaceText } from '@/utils/i18n';
import type { PitchInput, VideoPitch } from '@/types/pitch';

/** Mesmo limite do backend (PUT /videos/:id/pitches). */
export const MAX_PITCHES = 5;
export const MAX_PITCH_NAME_LENGTH = 40;
/** Teto do segundo de um pitch (24 h), mesmo com duração desconhecida — igual ao backend. */
export const MAX_PITCH_SECONDS = 86400;

/** Nome exibido do pitch: o nome salvo ou "Pitch N" pela ordem cronológica. */
export function pitchLabel(pitch: { name?: string | null }, index: number): string {
  const name = pitch.name?.trim();
  return name ? name : `Pitch ${index + 1}`;
}

/** "MM:SS" com minutos corridos (4530s → "75:30"), o formato aceito pelo PitchTimePicker. */
export function formatPitchTime(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Duração que limita o pitch, em segundos inteiros — ou null quando é desconhecida.
 * Mesma regra do backend: só vale número finito > 1 (o schema grava 1 por padrão e o
 * upload grava 0 sem metadado); fração vira o segundo inteiro alcançado (125.4 → 125).
 */
export function knownDuration(durationSeconds: number | null | undefined): number | null {
  const d = Number(durationSeconds);
  return Number.isFinite(d) && d > 1 ? Math.floor(d) : null;
}

/**
 * Mesmas regras e MESMAS mensagens do PUT /videos/:id/pitches (validatePitchesInput no
 * backend), que responde 422 com a primeira delas em `message`. "Pitch N" = posição na
 * lista enviada, a partir de 1. Devolve a primeira mensagem, ou null se a lista é válida.
 */
export function validatePitches(list: PitchInput[], durationSeconds?: number | null): string | null {
  if (!Array.isArray(list)) return translateInterfaceText("Envie a lista de pitches.");
  if (list.length > MAX_PITCHES) return `Máximo de ${MAX_PITCHES} pitches por vídeo.`;

  const limit = knownDuration(durationSeconds);
  const seen = new Set<number>();
  for (let index = 0; index < list.length; index++) {
    const item = list[index];
    const label = `Pitch ${index + 1}`;
    if (item === null || typeof item !== 'object' || Array.isArray(item)) {
      return `${label}: formato inválido.`;
    }
    const { seconds } = item;
    if (typeof seconds !== 'number' || !Number.isInteger(seconds) || seconds < 1) {
      return `${label}: o tempo precisa ser um número inteiro de segundos, a partir de 1.`;
    }
    if (seconds > MAX_PITCH_SECONDS) {
      return `${label}: o tempo passa do limite de 24 horas.`;
    }
    if (limit !== null && seconds > limit) {
      return `${label}: o tempo (${formatPitchTime(seconds)}) passa da duração do vídeo (${formatPitchTime(limit)}).`;
    }
    if (seen.has(seconds)) {
      return `${label}: já existe outro pitch em ${formatPitchTime(seconds)}.`;
    }
    seen.add(seconds);
    if ((item.name ?? '').trim().length > MAX_PITCH_NAME_LENGTH) {
      return `${label}: o nome pode ter no máximo ${MAX_PITCH_NAME_LENGTH} caracteres.`;
    }
  }

  return null;
}

/**
 * Lista completa para o PUT: troca o pitch `editingId` ou, com `editingId` null,
 * acrescenta um pitch novo (sem id). O PUT substitui a lista inteira, então os
 * outros pitches vão junto com os ids de sempre.
 */
export function upsertPitch(
  list: VideoPitch[],
  editingId: string | null,
  input: { name: string; seconds: number },
): PitchInput[] {
  const name = input.name.trim();
  const current: PitchInput[] = list.map((p) => ({ id: p.id, name: p.name, seconds: p.seconds }));
  if (editingId) {
    return current.map((p) => (p.id === editingId ? { id: p.id, name, seconds: input.seconds } : p));
  }
  return [...current, { name, seconds: input.seconds }];
}

/** Lista completa para o PUT sem o pitch `id`. */
export function removePitch(list: VideoPitch[], id: string): PitchInput[] {
  return list.filter((p) => p.id !== id).map((p) => ({ id: p.id, name: p.name, seconds: p.seconds }));
}

/** Pitches vindos das métricas (teste/duelo, com `start`) → lista do vídeo para editar. */
export function toVideoPitches(
  items: ReadonlyArray<{ id: string; name?: string | null; start: number }> | null | undefined,
): VideoPitch[] {
  return (items ?? []).map((p) => ({ id: p.id, name: p.name ?? '', seconds: p.start }));
}

/** Marca do funil: o ponto fica a menos de `windowSeconds` de algum pitch. */
export function isNearAnyPitch(timeSeconds: number, pitchSeconds: number[], windowSeconds = 15): boolean {
  return pitchSeconds.some((s) => Math.abs(timeSeconds - s) < windowSeconds);
}

/** Mensagem do 422 do backend (`{ message }`) ou um texto genérico. */
export function pitchSaveErrorMessage(error: unknown): string {
  const message = (error as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;
  return typeof message === 'string' && message.trim()
    ? message
    : translateInterfaceText("Não foi possível salvar o pitch. Tente novamente.");
}
